/**
 * The hosted page profile's editable cards, in the order the public page
 * shows them. Each is a ProfileSection (Section.tsx) that owns a slice of the
 * PUT body; Photos (Photos.tsx) saves as it goes and lives on its own.
 */
import type { ReactNode } from 'react';
import type { FieldIssue } from '../api/errors';
import type { HostedPageProfileInput, HostedPageProfileResponse, HostedPageTestimonial } from '../api/types';
import { Link } from '../router';
import { Field, Switch } from '../ui/kit';
import { ZipChips } from './ChipInput';
import { HoursGrid, hoursFromWeek, validateWeek, weekFromHours, type WeekForm } from './HoursGrid';
import { E164_RE, EMAIL_RE, LIMITS, formatPhone, quoteFormBlock, textOrNull, toE164, type ServiceAreaWire } from './model';
import { Repeater } from './Repeater';
import { ProfileSection } from './Section';

export interface SectionProps {
  slug: string;
  profile: HostedPageProfileInput;
  onSaved: (response: HostedPageProfileResponse) => void;
}

function count(value: string, max: number): string {
  return `${value.length}/${max} characters`;
}

function hint(text: ReactNode, value: string, max: number): ReactNode {
  return (
    <>
      {text ? <>{text} </> : null}
      {count(value, max)}.
    </>
  );
}

// ── Contact & calls ─────────────────────────────────────────────────────────
interface ContactForm {
  phone: string;
  show_call_button: boolean;
  email: string;
}

export function ContactSection(props: SectionProps) {
  return (
    <ProfileSection<ContactForm>
      {...props}
      id="hp-contact"
      title="Contact & calls"
      description="How homeowners reach you straight from your page."
      toForm={(p) => ({ phone: p.phone ?? '', show_call_button: p.show_call_button, email: p.email ?? '' })}
      toBody={(f) => ({ phone: f.phone.trim() ? toE164(f.phone) : null, show_call_button: f.show_call_button, email: textOrNull(f.email) })}
      validate={(f) => {
        const out: FieldIssue[] = [];
        if (f.phone.trim() && !E164_RE.test(toE164(f.phone))) {
          out.push({ path: 'phone', message: 'Enter a full phone number with area code, e.g. (206) 555-0100 or +44 20 7946 0000.' });
        }
        if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) out.push({ path: 'email', message: 'Enter an email address like you@example.com.' });
        return out;
      }}
    >
      {({ form, set, issues, disabled }) => {
        const normalised = toE164(form.phone);
        return (
          <>
            <Field
              label="Phone number"
              optional
              hint={
                E164_RE.test(normalised) ? `Shown on your page as ${formatPhone(normalised)}.` : 'The number homeowners call. US numbers can be typed any way you like.'
              }
              issues={issues('phone')}
            >
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={form.phone}
                  disabled={disabled}
                  placeholder="(206) 555-0100"
                  onChange={(e) => set({ ...form, phone: e.target.value })}
                  onBlur={() => {
                    if (E164_RE.test(normalised) && normalised !== form.phone) set({ ...form, phone: normalised });
                  }}
                />
              )}
            </Field>
            <Switch
              label="Show a Call button"
              description={form.phone.trim() ? 'A Call button at the top of your page dials this number.' : 'Add a phone number for the Call button to show.'}
              checked={form.show_call_button}
              disabled={disabled}
              onChange={(v) => set({ ...form, show_call_button: v })}
            />
            <Field label="Public email" optional hint="Shown on your page. Leads still arrive the way you set up in “Where leads go”." issues={issues('email')}>
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  disabled={disabled}
                  placeholder="office@example.com"
                  onChange={(e) => set({ ...form, email: e.target.value })}
                />
              )}
            </Field>
          </>
        );
      }}
    </ProfileSection>
  );
}

// ── Service area ────────────────────────────────────────────────────────────
interface AreaForm {
  summary: string;
  codes: string[];
}

export function ServiceAreaSection({ wire = 'flat', ...props }: SectionProps & { wire?: ServiceAreaWire }) {
  return (
    <ProfileSection<AreaForm>
      {...props}
      id="hp-area"
      title="Service area"
      description="Where you take jobs. The summary is what visitors read; the ZIP codes help search engines."
      toForm={(p) => ({ summary: p.service_area_summary ?? '', codes: p.service_area_postal_codes })}
      toBody={(f) =>
        wire === 'nested'
          ? { service_area: { summary: textOrNull(f.summary), postal_codes: f.codes } }
          : { service_area_summary: textOrNull(f.summary), service_area_postal_codes: f.codes }
      }
    >
      {({ form, set, issues, disabled }) => (
        <>
          <Field
            label="Summary"
            optional
            hint={hint('E.g. “Seattle, Shoreline and Lynnwood”.', form.summary, LIMITS.serviceAreaSummary)}
            issues={issues('service_area_summary')}
          >
            {(p) => (
              <input
                {...p}
                className="input"
                maxLength={LIMITS.serviceAreaSummary}
                value={form.summary}
                disabled={disabled}
                onChange={(e) => set({ ...form, summary: e.target.value })}
              />
            )}
          </Field>
          <ZipChips
            label="ZIP codes and prefixes"
            hint={`Whole ZIP codes (98103) or the first 3 or 4 digits of several (981). Up to ${LIMITS.postalCodes}.`}
            values={form.codes}
            max={LIMITS.postalCodes}
            disabled={disabled}
            onChange={(codes) => set({ ...form, codes })}
          />
        </>
      )}
    </ProfileSection>
  );
}

// ── Trades ──────────────────────────────────────────────────────────────────
export function TradesSection({ options, ...props }: SectionProps & { options: Array<{ code: string; label: string }> }) {
  return (
    <ProfileSection<{ trades: string[] }>
      {...props}
      id="hp-trades"
      title="Trades"
      description="What you do. The first one you tick shapes how search engines describe your business."
      toForm={(p) => ({ trades: p.trades })}
      toBody={(f) => ({ trades: f.trades })}
    >
      {({ form, set, disabled }) => {
        const known = new Set(options.map((o) => o.code));
        const extra = form.trades.filter((t) => !known.has(t)).map((code) => ({ code, label: code }));
        const all = [...options, ...extra];
        const toggle = (code: string, on: boolean) => {
          const kept = on ? [...form.trades, code] : form.trades.filter((t) => t !== code);
          // Keep the order the partner ticked them in: the first is the primary trade.
          set({ trades: [...new Set(kept)] });
        };
        return (
          <fieldset>
            <legend className="visually-hidden">Trades you offer</legend>
            {all.length ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))', gap: 8 }}>
                {all.map((o) => {
                  const id = `hp-trade-${o.code}`;
                  return (
                    <span key={o.code} className="check">
                      <input id={id} type="checkbox" checked={form.trades.includes(o.code)} disabled={disabled} onChange={(e) => toggle(o.code, e.target.checked)} />
                      <label htmlFor={id}>{o.label}</label>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="small muted" style={{ margin: 0 }}>
                No trades to choose from yet.
              </p>
            )}
          </fieldset>
        );
      }}
    </ProfileSection>
  );
}

// ── Services ────────────────────────────────────────────────────────────────
interface ServiceRow {
  name: string;
  description: string;
}

export function ServicesSection(props: SectionProps) {
  return (
    <ProfileSection<{ services: ServiceRow[] }>
      {...props}
      id="hp-services"
      title="Services"
      description="The jobs you want more of, each with a line about it. Two or more help visitors see you do their job."
      toForm={(p) => ({ services: p.services.map((s) => ({ name: s.name, description: s.description ?? '' })) })}
      toBody={(f) => ({
        services: f.services
          .filter((s) => s.name.trim() || s.description.trim())
          .map((s) => ({ name: s.name.trim(), description: textOrNull(s.description) })),
      })}
      validate={(f) =>
        f.services.flatMap((s, i) => (!s.name.trim() && s.description.trim() ? [{ path: `services.${i}.name`, message: 'Give this service a name.' }] : []))
      }
    >
      {({ form, set, issues, disabled }) => (
        <Repeater<ServiceRow>
          items={form.services}
          onChange={(services) => set({ services })}
          max={LIMITS.services}
          noun="Service"
          addLabel="Add a service"
          make={() => ({ name: '', description: '' })}
          disabled={disabled}
        >
          {(s, update, i) => (
            <>
              <Field label="Name" hint={count(s.name, LIMITS.serviceName)} issues={issues(`services.${i}.name`)}>
                {(p) => (
                  <input {...p} className="input" maxLength={LIMITS.serviceName} value={s.name} disabled={disabled} placeholder="Water heater repair" onChange={(e) => update({ ...s, name: e.target.value })} />
                )}
              </Field>
              <Field label="Description" optional hint={count(s.description, LIMITS.serviceDescription)} issues={issues(`services.${i}.description`)}>
                {(p) => (
                  <textarea
                    {...p}
                    className="input"
                    rows={2}
                    maxLength={LIMITS.serviceDescription}
                    value={s.description}
                    disabled={disabled}
                    onChange={(e) => update({ ...s, description: e.target.value })}
                  />
                )}
              </Field>
            </>
          )}
        </Repeater>
      )}
    </ProfileSection>
  );
}

// ── About & credentials ─────────────────────────────────────────────────────
interface AboutForm {
  about: string;
  founded_year: string;
  license_number: string;
  license_state: string;
  license_authority: string;
  insured: boolean;
  insurance_note: string;
}

export function AboutSection(props: SectionProps) {
  const thisYear = new Date().getFullYear();
  return (
    <ProfileSection<AboutForm>
      {...props}
      id="hp-about"
      title="About & credentials"
      description="Who you are, how long you’ve done it, and the license and insurance that let homeowners trust you."
      toForm={(p) => ({
        about: p.about ?? '',
        founded_year: p.founded_year === null ? '' : String(p.founded_year),
        license_number: p.license?.number ?? '',
        license_state: p.license?.state ?? '',
        license_authority: p.license?.authority ?? '',
        insured: p.insured,
        insurance_note: p.insurance_note ?? '',
      })}
      toBody={(f) => ({
        about: textOrNull(f.about),
        founded_year: f.founded_year.trim() ? Number(f.founded_year) : null,
        license: f.license_number.trim()
          ? { number: f.license_number.trim(), state: textOrNull(f.license_state)?.toUpperCase() ?? null, authority: textOrNull(f.license_authority) }
          : null,
        insured: f.insured,
        insurance_note: textOrNull(f.insurance_note),
      })}
      validate={(f) => {
        const out: FieldIssue[] = [];
        const y = f.founded_year.trim();
        if (y) {
          const n = Number(y);
          if (!Number.isInteger(n) || n < LIMITS.foundedMin || n > Math.min(LIMITS.foundedMax, thisYear)) {
            out.push({ path: 'founded_year', message: `Enter a year between ${LIMITS.foundedMin} and ${thisYear}.` });
          }
        }
        if (f.license_state.trim() && !/^[A-Za-z]{2}$/.test(f.license_state.trim())) {
          out.push({ path: 'license.state', message: 'Use the two-letter state code, e.g. WA.' });
        }
        if (!f.license_number.trim() && (f.license_state.trim() || f.license_authority.trim())) {
          out.push({ path: 'license.number', message: 'Add the license number too, or clear the state and issuer.' });
        }
        return out;
      }}
    >
      {({ form, set, issues, disabled }) => (
        <>
          <Field label="About your business" optional hint={hint('Plain text. What you do best, who does the work, why people call you back.', form.about, LIMITS.about)} issues={issues('about')}>
            {(p) => (
              <textarea {...p} className="input" rows={6} maxLength={LIMITS.about} value={form.about} disabled={disabled} onChange={(e) => set({ ...form, about: e.target.value })} />
            )}
          </Field>
          <Field label="Year founded" optional hint="Your page shows how many years you’ve been in business." issues={issues('founded_year')}>
            {(p) => (
              <input
                {...p}
                className="input num"
                style={{ maxWidth: 140 }}
                type="number"
                inputMode="numeric"
                min={LIMITS.foundedMin}
                max={thisYear}
                value={form.founded_year}
                disabled={disabled}
                onChange={(e) => set({ ...form, founded_year: e.target.value })}
              />
            )}
          </Field>
          <fieldset className="stack-sm">
            <legend className="label" style={{ marginBottom: 4 }}>
              License
            </legend>
            <div className="grid-3">
              <Field label="License number" optional issues={issues('license.number')}>
                {(p) => (
                  <input
                    {...p}
                    className="input mono"
                    maxLength={LIMITS.licenseNumber}
                    value={form.license_number}
                    disabled={disabled}
                    onChange={(e) => set({ ...form, license_number: e.target.value })}
                  />
                )}
              </Field>
              <Field label="State" optional issues={issues('license.state')}>
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    style={{ textTransform: 'uppercase' }}
                    maxLength={2}
                    autoCapitalize="characters"
                    placeholder="WA"
                    value={form.license_state}
                    disabled={disabled}
                    onChange={(e) => set({ ...form, license_state: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Issued by" optional issues={issues('license.authority')}>
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    maxLength={LIMITS.licenseAuthority}
                    placeholder="Washington L&I"
                    value={form.license_authority}
                    disabled={disabled}
                    onChange={(e) => set({ ...form, license_authority: e.target.value })}
                  />
                )}
              </Field>
            </div>
          </fieldset>
          <Switch
            label="Insured"
            description="Shows “Insured” on your page. Only switch this on if your policy is current."
            checked={form.insured}
            disabled={disabled}
            onChange={(v) => set({ ...form, insured: v })}
          />
          <Field label="Insurance note" optional hint={hint('E.g. “$2M general liability”.', form.insurance_note, LIMITS.insuranceNote)} issues={issues('insurance_note')}>
            {(p) => (
              <input
                {...p}
                className="input"
                maxLength={LIMITS.insuranceNote}
                value={form.insurance_note}
                disabled={disabled}
                onChange={(e) => set({ ...form, insurance_note: e.target.value })}
              />
            )}
          </Field>
        </>
      )}
    </ProfileSection>
  );
}

// ── Hours ───────────────────────────────────────────────────────────────────
interface HoursForm {
  week: WeekForm;
  emergency: boolean;
  note: string;
}

export function HoursSection(props: SectionProps) {
  return (
    <ProfileSection<HoursForm>
      {...props}
      id="hp-hours"
      title="Hours"
      description="When you answer the phone. Tick Closed for days off."
      toForm={(p) => ({ week: weekFromHours(p.hours), emergency: p.hours?.emergency_24_7 ?? false, note: p.hours_note ?? '' })}
      toBody={(f) => ({ hours: hoursFromWeek(f.week, f.emergency), hours_note: textOrNull(f.note) })}
      validate={(f) => validateWeek(f.week)}
    >
      {({ form, set, issues, disabled }) => (
        <>
          <HoursGrid week={form.week} disabled={disabled} issues={issues} onChange={(week) => set({ ...form, week })} />
          <Switch
            label="24/7 emergency service"
            description="Your page says you take emergency calls around the clock."
            checked={form.emergency}
            disabled={disabled}
            onChange={(v) => set({ ...form, emergency: v })}
          />
          <Field label="Hours note" optional hint={hint('E.g. “Evenings by appointment”.', form.note, LIMITS.hoursNote)} issues={issues('hours_note')}>
            {(p) => <input {...p} className="input" maxLength={LIMITS.hoursNote} value={form.note} disabled={disabled} onChange={(e) => set({ ...form, note: e.target.value })} />}
          </Field>
        </>
      )}
    </ProfileSection>
  );
}

// ── Testimonials ────────────────────────────────────────────────────────────
interface TestimonialRow {
  author: string;
  text: string;
  rating: string;
  date: string;
}

export function TestimonialsSection(props: SectionProps) {
  return (
    <ProfileSection<{ items: TestimonialRow[] }>
      {...props}
      id="hp-testimonials"
      title="Testimonials"
      description="What customers said, in their words and with their permission. Your page shows them as quotes."
      toForm={(p) => ({ items: p.testimonials.map((t) => ({ author: t.author, text: t.text, rating: t.rating === null ? '' : String(t.rating), date: t.date ?? '' })) })}
      toBody={(f) => ({
        testimonials: f.items
          .filter((t) => t.author.trim() || t.text.trim())
          .map((t) => ({
            author: t.author.trim(),
            text: t.text.trim(),
            rating: t.rating ? (Number(t.rating) as HostedPageTestimonial['rating']) : null,
            date: t.date || null,
          })),
      })}
      validate={(f) =>
        f.items.flatMap((t, i) => {
          if (!t.author.trim() && !t.text.trim()) return [];
          const out: FieldIssue[] = [];
          if (!t.author.trim()) out.push({ path: `testimonials.${i}.author`, message: 'Add who said it, e.g. “Dana, Ballard”.' });
          if (!t.text.trim()) out.push({ path: `testimonials.${i}.text`, message: 'Add what they said.' });
          return out;
        })
      }
    >
      {({ form, set, issues, disabled }) => (
        <Repeater<TestimonialRow>
          items={form.items}
          onChange={(items) => set({ items })}
          max={LIMITS.testimonials}
          noun="Testimonial"
          addLabel="Add a testimonial"
          make={() => ({ author: '', text: '', rating: '', date: '' })}
          disabled={disabled}
        >
          {(t, update, i) => (
            <>
              <Field label="What they said" hint={count(t.text, LIMITS.testimonialText)} issues={issues(`testimonials.${i}.text`)}>
                {(p) => (
                  <textarea {...p} className="input" rows={3} maxLength={LIMITS.testimonialText} value={t.text} disabled={disabled} onChange={(e) => update({ ...t, text: e.target.value })} />
                )}
              </Field>
              <div className="grid-3">
                <Field label="Who said it" issues={issues(`testimonials.${i}.author`)}>
                  {(p) => (
                    <input {...p} className="input" maxLength={LIMITS.testimonialAuthor} placeholder="Dana, Ballard" value={t.author} disabled={disabled} onChange={(e) => update({ ...t, author: e.target.value })} />
                  )}
                </Field>
                <Field label="Rating" optional>
                  {(p) => (
                    <select {...p} className="input" value={t.rating} disabled={disabled} onChange={(e) => update({ ...t, rating: e.target.value })}>
                      <option value="">No rating</option>
                      {[5, 4, 3, 2, 1].map((n) => (
                        <option key={n} value={String(n)}>
                          {n} {n === 1 ? 'star' : 'stars'}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Date" optional issues={issues(`testimonials.${i}.date`)}>
                  {(p) => <input {...p} className="input" type="date" value={t.date} disabled={disabled} onChange={(e) => update({ ...t, date: e.target.value })} />}
                </Field>
              </div>
            </>
          )}
        </Repeater>
      )}
    </ProfileSection>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────
interface FaqRow {
  q: string;
  a: string;
}

export function FaqSection(props: SectionProps) {
  return (
    <ProfileSection<{ items: FaqRow[] }>
      {...props}
      id="hp-faq"
      title="FAQ"
      saveLabel="Save FAQ"
      description="The questions you answer on every call: call-out fees, areas, how soon you can come."
      toForm={(p) => ({ items: p.faq.map((f) => ({ q: f.q, a: f.a })) })}
      toBody={(f) => ({ faq: f.items.filter((x) => x.q.trim() || x.a.trim()).map((x) => ({ q: x.q.trim(), a: x.a.trim() })) })}
      validate={(f) =>
        f.items.flatMap((x, i) => {
          if (!x.q.trim() && !x.a.trim()) return [];
          const out: FieldIssue[] = [];
          if (!x.q.trim()) out.push({ path: `faq.${i}.q`, message: 'Add the question.' });
          if (!x.a.trim()) out.push({ path: `faq.${i}.a`, message: 'Add the answer.' });
          return out;
        })
      }
    >
      {({ form, set, issues, disabled }) => (
        <Repeater<FaqRow>
          items={form.items}
          onChange={(items) => set({ items })}
          max={LIMITS.faq}
          noun="Question"
          addLabel="Add a question"
          make={() => ({ q: '', a: '' })}
          disabled={disabled}
        >
          {(x, update, i) => (
            <>
              <Field label="Question" hint={count(x.q, LIMITS.faqQuestion)} issues={issues(`faq.${i}.q`)}>
                {(p) => <input {...p} className="input" maxLength={LIMITS.faqQuestion} value={x.q} disabled={disabled} onChange={(e) => update({ ...x, q: e.target.value })} />}
              </Field>
              <Field label="Answer" hint={count(x.a, LIMITS.faqAnswer)} issues={issues(`faq.${i}.a`)}>
                {(p) => <textarea {...p} className="input" rows={3} maxLength={LIMITS.faqAnswer} value={x.a} disabled={disabled} onChange={(e) => update({ ...x, a: e.target.value })} />}
              </Field>
            </>
          )}
        </Repeater>
      )}
    </ProfileSection>
  );
}

// ── Calls to action ─────────────────────────────────────────────────────────
interface CtaForm {
  primary_label: string;
  quote_label: string;
  quote_intro: string;
  quote_enabled: boolean;
}

export function CtaSection({ quoteForm, ...props }: SectionProps & { quoteForm: HostedPageProfileResponse['quote_form'] }) {
  const block = quoteFormBlock(quoteForm.reason);
  return (
    <ProfileSection<CtaForm>
      {...props}
      id="hp-cta"
      title="Calls to action"
      description="The buttons on your page. Leave a label empty for our default wording."
      toForm={(p) => ({
        primary_label: p.cta.primary_label ?? '',
        quote_label: p.cta.quote_label ?? '',
        quote_intro: p.cta.quote_intro ?? '',
        quote_enabled: p.cta.quote_enabled,
      })}
      toBody={(f) => ({
        cta: {
          primary_label: textOrNull(f.primary_label),
          quote_label: textOrNull(f.quote_label),
          quote_intro: textOrNull(f.quote_intro),
          quote_enabled: f.quote_enabled,
        },
      })}
    >
      {({ form, set, issues, disabled }) => (
        <>
          <Field label="Main button" optional hint={hint('The button that opens the photo diagnosis, e.g. “Show us the problem”.', form.primary_label, LIMITS.ctaLabel)} issues={issues('cta.primary_label')}>
            {(p) => (
              <input {...p} className="input" maxLength={LIMITS.ctaLabel} value={form.primary_label} disabled={disabled} onChange={(e) => set({ ...form, primary_label: e.target.value })} />
            )}
          </Field>
          <Switch
            label="Request a quote form"
            description={
              block?.blocksSwitch && !form.quote_enabled
                ? 'Not available yet — see below.'
                : 'A short form on your page: name, phone, ZIP and what they need. Requests arrive as leads.'
            }
            checked={form.quote_enabled}
            // A block only stops switching it ON; a form that is on can always be switched off.
            disabled={disabled || (Boolean(block?.blocksSwitch) && !form.quote_enabled)}
            onChange={(v) => set({ ...form, quote_enabled: v })}
          />
          {block ? (
            <div className="notice warn" role="note">
              <div className="stack-sm" style={{ gap: 4 }}>
                <span>{block.message}</span>
                {block.fix ? (
                  <span>
                    <Link to={block.fix.to}>{block.fix.label}</Link>
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
          <Field label="Quote button" optional hint={hint('E.g. “Get a free quote”.', form.quote_label, LIMITS.ctaLabel)} issues={issues('cta.quote_label')}>
            {(p) => <input {...p} className="input" maxLength={LIMITS.ctaLabel} value={form.quote_label} disabled={disabled} onChange={(e) => set({ ...form, quote_label: e.target.value })} />}
          </Field>
          <Field label="Quote form intro" optional hint={hint('A line above the form, e.g. “We reply within 2 hours on weekdays.”', form.quote_intro, LIMITS.quoteIntro)} issues={issues('cta.quote_intro')}>
            {(p) => (
              <textarea {...p} className="input" rows={2} maxLength={LIMITS.quoteIntro} value={form.quote_intro} disabled={disabled} onChange={(e) => set({ ...form, quote_intro: e.target.value })} />
            )}
          </Field>
        </>
      )}
    </ProfileSection>
  );
}
